/**
 * Centralized tuning configuration for game balance.
 * All tunable values live here so they can be adjusted from the dev panel.
 *
 * Usage: import { tuning } from '../data/tuning';
 * The object is mutable — the TuningPanel modifies it in place and
 * triggers a React state update to propagate changes.
 */

import { ZoneType } from '../types/map';

// --- Card tuning ---

export interface CardTuning {
  apCost: number;
  movementRange?: number;
  signalModifier?: number;
  sweepRadius?: number;
}

// --- Global tuning ---

export interface TuningConfig {
  // Player
  playerMaxAp: number;
  handSize: number;
  visionConeAngle: number;     // degrees
  visionConeRange: number;     // meters
  hideRange: number;            // meters — max distance to enter a building
  hideDuration: number;         // turns of zero signal
  instinctDuration: number;     // turns of 360° vision

  // Rooftop
  rooftopConeAngle: number;        // degrees — wider cone when elevated
  rooftopRangeMultiplier: number;  // base range multiplier when elevated (before binoculars)
  rooftopBinocularRevealDuration: number; // turns agent stays revealed after binocular rooftop sighting
  sniperRifleRange: number;        // meters — vision/kill range when on rooftop with sniper rifle
  sniperConeAngle: number;         // degrees — cone width when holding sniper rifle on rooftop
  sniperConeAngleWithBinoculars: number; // degrees — cone width when holding sniper + binoculars

  // Extraction
  extractRange: number;          // meters — must be within this to play Extract card
  extractRevealRange: number;    // meters — within this, player position broadcast to agent
  extractMaxExposurePct: number; // 0-1 — max exposure % for clean extraction (e.g. 0.2 = 20%)

  // Decoy
  decoyStrength: number;        // 0-1
  decoyRadius: number;          // meters

  // Burner phone
  burnerPhoneStrength: number;  // 0-1 (stronger = more convincing)
  burnerPhoneRadius: number;    // meters (smaller = tighter, more precise fake signal)

  // Informant
  informantMinRange: number;    // meters — min distance for POI from agent
  informantMaxRange: number;    // meters — max distance for POI from agent
  informantTipDuration: number; // turns before tip fades

  // Spotter
  spotterRadius: number;        // meters
  spotterDuration: number;      // turns

  // Stakeout — NPC observer on a commercial building rooftop
  stakeoutConeAngle: number;    // degrees — total cone width
  stakeoutConeRange: number;    // meters — cone sight range
  stakeoutSeenDuration: number; // turns of "enemy seen" fade after agent exits the cone

  // Surveillance Van — mobile observer patrolling a vehicle route
  surveillanceVanRouteBudget: number;   // meters — max route length at deploy
  surveillanceVanStepPerTurn: number;   // meters — how far the van advances each turn
  surveillanceVanVisionRange: number;   // meters — 360° cone radius
  surveillanceVanSeenDuration: number;  // turns — "enemy seen" fade after last contact

  // Transit
  transitBoardRange: number;      // meters — max distance from stop to board
  transitBoardSignal: number;     // signal strength generated on board/exit
  transitRideSignalSubway: number; // signal generated while riding subway
  transitRideSignalTram: number;  // signal generated while riding tram/bus
  transitRideSignalBus: number;   // signal generated while riding bus
  transitRideSignalFerry: number; // signal generated while riding ferry
  transitExposurePerTurn: number; // exposure accumulated per turn while riding

  // Taxi
  taxiSpeedPerTurn: number;       // meters — distance the taxi auto-advances per turn
  taxiSignalMultiplier: number;   // multiplier on movement signal while in taxi (1.5 = +50% louder than walking)
  taxiExposureMultiplier: number; // multiplier on raw exposure while in taxi

  // Disguise
  disguiseDuration: number;         // turns the disguise stays active
  disguiseSignalMultiplier: number; // multiplier applied on commercial/major segments (0.25 = 75% reduction)
  disguiseExposureMultiplier: number; // multiplier on exposure gain while active (0.5 = 50% reduction)

  // Signal system
  idleDampening: number;        // multiplier for idle signal (default 0.5)
  distanceNormalization: number; // meters — distance at which distance bonus maxes out
  minSignalThreshold: number;   // below this, no signal generated
  baseSignalFloor: number;      // minimum signal for any movement (before card modifier)
  signalAreaWeight: number;     // how much of signal comes from zone/road (0-1, default 0.7)
  signalDistanceWeight: number; // how much distance adds on top (0-1, default 0.3)
  minSearchRadius: number;      // meters — radius at max signal strength (1.0)
  maxSearchRadius: number;      // meters — radius at minimum signal threshold
  searchRadiusCurve: number;    // >1 = tighter weak signals, <1 = looser weak signals

  // Exposure — accumulated zone exposure (0 to exposureMax scale)
  exposureMax: number;           // max exposure points (e.g. 1000)
  exposureDecayPerTurn: number;  // how many points decay at end of each turn
  exposureMaxEffect: number;     // max signal multiplier at full exposure (e.g. 2.0 = doubles signal)
  exposureZoneThreshold: number; // zone multiplier above which zone exposure accumulates (e.g. 1.0)

  // Awareness — scales with exposure: more exposed = more aware of surroundings
  awarenessThresholds: {
    /** Below this: UI label "blind" (cosmetic only, no hard gate) */
    blind: number;
    /** Between blind and full: UI label "partial" */
    partial: number;
    /** Above this: full awareness — agent signals shown at true strength */
    full: number;
  };
  /** At 0 exposure, agent signal displayed strength is scaled by this factor (0-1).
   *  Scales linearly up to 1.0 at full awareness threshold. */
  awarenessMinMultiplier: number;

  // Marks economy
  marks: {
    killReward: number;
    escapeReward: number;
    passphraseReward: number;
    deathPenaltyPct: number;      // fraction of unbanked marks lost on death (0.5 = 50%)
    shopDraftBaseCost: number;
    shopDraftCostMultiplier: number;
    shopRemovalCost: number;
    shopDirectMoveCost: number;
    shopDirectShortcutCost: number;
    minDeckSize: number;          // won't force card drops below this
  };

  // Zone multipliers
  zoneMultipliers: Record<ZoneType, number>;

  // Agent AI — general
  agentMoveRange: number;       // meters per move (legacy, used as patrol speed)
  agentMovesPerTurn: number;

  // Agent AI — per-state speeds (meters per turn)
  agentSpeed: {
    search: number;       // SEARCH state — moderate patrol speed
    searchNarrowing: number; // SEARCH with confidence 0.3-0.5 — deliberate approach
    pursue: number;       // PURSUE state — fast, closing distance
    engage: number;       // ENGAGE state — positioning, not sprinting
  };

  // Agent AI — belief system
  agentBelief: {
    /** Confidence decay multiplier per turn without new info (e.g., 0.88) */
    decayRate: number;
    /** Base uncertainty radius in meters at confidence 1.0 */
    baseUncertainty: number;
    /** Max uncertainty radius cap (meters) */
    maxRadius: number;
    /** Confidence boost per corroborating signal (scaled by strength × credibility) */
    corroborationBonus: number;
    /** Confidence multiplier when a signal contradicts current belief */
    contradictionPenalty: number;
    /** Confidence multiplier when agent arrives at belief and finds nothing */
    negativeIntelPenalty: number;
    /** Confidence multiplier when agent sweep finds nothing near belief */
    sweepNegativePenalty: number;
    /** Turns before cleared areas expire */
    clearedAreaExpiry: number;
    /** Minimum signals needed to infer heading */
    headingMinSignals: number;
    /** Only use signals from last N turns for heading inference */
    headingMaxAge: number;
    /** How many recent signals to keep in history */
    signalHistoryLength: number;
  };

  // Agent AI — state transition thresholds (confidence-based)
  agentStateThresholds: {
    /** Confidence to transition from SEARCH → PURSUE */
    searchToPursue: number;
    /** Confidence below which PURSUE → SEARCH */
    pursueToSearch: number;
    /** Confidence to transition from PURSUE → ENGAGE */
    pursueToEngage: number;
    /** Distance (meters) — also trigger ENGAGE if within this AND confidence ≥ 0.7 */
    engageProximity: number;
    /** Confidence below which ENGAGE → PURSUE */
    engageToPursue: number;
    /** Confidence below which ENGAGE → SEARCH */
    engageToSearch: number;
  };

  // Agent AI — card system
  agentCardSystem: {
    handSize: number;
    baseAp: number;
    /** Minimum card score to bother playing (below this, skip) */
    minPlayThreshold: number;
    /** How many recent visited nodes to remember */
    visitedNodeMemory: number;
  };

  // Agent AI — per-archetype goal weight modifiers (added to base goal values)
  agentGoalWeights: Record<string, { prep: number; distance: number; intel: number; position: number }>;

  // Cards — per-card overrides
  cards: Record<string, CardTuning>;
}

// Default values — these match the current hard-coded values
export const DEFAULT_TUNING: TuningConfig = {
  playerMaxAp: 3,
  handSize: 4,
  visionConeAngle: 90,
  visionConeRange: 100,
  hideRange: 50,
  hideDuration: 3,
  instinctDuration: 1,

  rooftopConeAngle: 120,
  rooftopRangeMultiplier: 2,
  rooftopBinocularRevealDuration: 5,
  sniperRifleRange: 400,            // 400m kill range on rooftop with sniper rifle
  sniperConeAngle: 90,              // tight cone when scoped in
  sniperConeAngleWithBinoculars: 110, // wider with binoculars to help spot targets

  extractRange: 50,
  extractRevealRange: 300,
  extractMaxExposurePct: 0.35,

  decoyStrength: 0.85,
  decoyRadius: 350,

  burnerPhoneStrength: 0.9,
  burnerPhoneRadius: 200,

  informantMinRange: 100,
  informantMaxRange: 500,
  informantTipDuration: 3,

  spotterRadius: 200,
  // Stakeout defaults — captured into each device at placement time
  stakeoutConeAngle: 120,
  stakeoutConeRange: 250,
  stakeoutSeenDuration: 3,
  // Surveillance Van defaults — captured into each device at placement time
  surveillanceVanRouteBudget: 600,
  surveillanceVanStepPerTurn: 200,
  surveillanceVanVisionRange: 200,
  surveillanceVanSeenDuration: 3,
  spotterDuration: 3,

  transitBoardRange: 50,
  transitBoardSignal: 0.7,
  transitRideSignalSubway: 0.3,
  transitRideSignalTram: 0.15,
  transitRideSignalBus: 0.15,
  transitRideSignalFerry: 0.1,
  transitExposurePerTurn: 30,

  taxiSpeedPerTurn: 600,
  taxiSignalMultiplier: 1.5,
  taxiExposureMultiplier: 1.3,

  disguiseDuration: 3,
  disguiseSignalMultiplier: 0.25,
  disguiseExposureMultiplier: 0.5,

  idleDampening: 0.85,
  distanceNormalization: 600,
  minSignalThreshold: 0.05,
  baseSignalFloor: 0.15,       // minimum signal for any movement (before card modifier)
  signalAreaWeight: 0.7,       // 70% of signal comes from zone/road type
  signalDistanceWeight: 0.3,   // 30% additional from distance (sqrt curve)
  minSearchRadius: 50,        // tightest circle at strength=1.0
  maxSearchRadius: 1200,      // widest circle at weakest detectable signal
  searchRadiusCurve: 1.5,     // curve exponent — higher = tighter weak signals

  exposureMax: 1000,             // max exposure points
  exposureDecayPerTurn: 50,     // loses 50 points per turn passively
  exposureMaxEffect: 3.0,       // at 100% exposure, triples your signal strength
  exposureZoneThreshold: 1.0,   // zones with multiplier >= 1.0 generate zone exposure

  awarenessThresholds: {
    blind: 200,       // below 200 (20%): UI shows "blind" label
    partial: 400,     // 200-600: UI shows "partial" label
    full: 600,        // above 600 (60%): full awareness — signals at true strength
  },
  awarenessMinMultiplier: 0.15,

  marks: {
    killReward: 200,
    // Extract/escape no longer gives a flat marks bonus — marks should
    // come only from items sold at extract and from kills. Banking is
    // itself the reward; no "survived the round" tip on top.
    escapeReward: 0,
    passphraseReward: 25,
    deathPenaltyPct: 0.5,
    shopDraftBaseCost: 100,
    shopDraftCostMultiplier: 2,
    shopRemovalCost: 150,
    shopDirectMoveCost: 100,
    shopDirectShortcutCost: 150,
    minDeckSize: 6,
  }, // at 0 exposure, agent signals shown at 15% strength (very imprecise)

  zoneMultipliers: {
    commercial: 1.5,
    transit: 1.8,
    'mixed-use': 1.4,
    civic: 1.2,
    park: 0,
    residential: 0.85,
    industrial: 0.6,
  },

  agentMoveRange: 300,
  agentMovesPerTurn: 2,

  // Agent base movement now matches the player's Move card (300m per move)
  // across all AI states. Previously the agent out-sped the player in every
  // state (400–450 vs 300), which made evasion mathematically unfair: even a
  // perfect-play player couldn't outrun the agent on a straight line. With
  // equal base speed, positional play and cover become the deciding factors
  // instead of raw distance racing.
  agentSpeed: {
    search: 300,
    searchNarrowing: 300,
    pursue: 300,
    engage: 300,
  },

  agentBelief: {
    decayRate: 0.92,             // slower decay — was 0.88, too aggressive
    baseUncertainty: 50,
    maxRadius: 2000,
    corroborationBonus: 0.4,     // doubled from 0.2 — strong signals should build confidence fast
    contradictionPenalty: 0.85,
    negativeIntelPenalty: 0.75,      // was 0.5 — halving was too harsh, caused death spiral
    sweepNegativePenalty: 0.7,
    clearedAreaExpiry: 8,
    headingMinSignals: 2,
    headingMaxAge: 4,
    signalHistoryLength: 20,
  },

  agentStateThresholds: {
    searchToPursue: 0.5,
    pursueToSearch: 0.5,
    pursueToEngage: 0.85,
    engageProximity: 200,
    engageToPursue: 0.7,
    engageToSearch: 0.5,
  },

  agentCardSystem: {
    handSize: 4,
    baseAp: 3,
    minPlayThreshold: 0.1,
    visitedNodeMemory: 20,
  },

  agentGoalWeights: {
    tracker: { prep: 0, distance: 0.15, intel: 0, position: 0 },
    enforcer: { prep: 0.15, distance: 0, intel: 0, position: 0 },
    ghost: { prep: 0, distance: 0, intel: 0, position: 0.1 },
    sniper: { prep: 0, distance: 0, intel: 0, position: 0.2 },
  },

  cards: {
    move:                { apCost: 1, movementRange: 300 },
    shortcut:            { apCost: 1, movementRange: 150 },
    quickStep:           { apCost: 1 },
    sprint:              { apCost: 1 },
    fade:                { apCost: 1 },
    sweep:               { apCost: 1, sweepRadius: 500 },
    counterSurveillance: { apCost: 0 },
    informant:           { apCost: 2 },
    spotter:             { apCost: 2 },
    hideInside:          { apCost: 1 },
    decoy:               { apCost: 3 },
    improvisedStrike:    { apCost: 1 },
    instinct:            { apCost: 1 },
    hotwire:             { apCost: 3 },
    taxi:                { apCost: 3, movementRange: 600 },
    disguise:            { apCost: 2 },
    malware:             { apCost: 2 },
    stageExit:           { apCost: 2 },
    doubleAgent:         { apCost: 1 },
    lowProfile:          { apCost: 1 },
    paradeRoute:         { apCost: 1 },
    stopAndFrisk:        { apCost: 2 },
    bearing:             { apCost: 1 },
    reconPlane:          { apCost: 1 },
    stakeout:            { apCost: 3 },
    surveillanceVan:     { apCost: 3 },
    trafficAnalysis:     { apCost: 2 },
    rooftopAccess:       { apCost: 3 },
    adrenaline:          { apCost: 0 },
    detour:              { apCost: 0 },
    calculated:          { apCost: 1 },
    adapt:               { apCost: 1 },
    copy:                { apCost: 2 },
    recall:              { apCost: 1 },
    stash:               { apCost: 0 },
    fieldModify:         { apCost: 1 },
    overclock:           { apCost: 1 },
    sharpen:             { apCost: 1 },
    patience:            { apCost: 0 },
    opportunist:         { apCost: 0 },
    coldRead:            { apCost: 1 },
    operationalIntegrity:{ apCost: 0 },
    improvise:           { apCost: 0 },
    doubleTap:           { apCost: 1 },
    contingency:         { apCost: 1 },
  },
};

const TUNING_STORAGE_KEY = 'geo-sleeper-tuning';

/** Load saved tuning from localStorage, merging with defaults for any missing keys */
function loadTuning(): TuningConfig {
  try {
    const saved = localStorage.getItem(TUNING_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Deep merge: start from defaults, overlay saved values
      const merged: TuningConfig = JSON.parse(JSON.stringify(DEFAULT_TUNING));
      for (const key of Object.keys(merged) as (keyof TuningConfig)[]) {
        if (key === 'zoneMultipliers' && parsed.zoneMultipliers) {
          Object.assign(merged.zoneMultipliers, parsed.zoneMultipliers);
        } else if (key === 'cards' && parsed.cards) {
          for (const cardKey of Object.keys(merged.cards)) {
            if (parsed.cards[cardKey]) {
              Object.assign(merged.cards[cardKey], parsed.cards[cardKey]);
            }
          }
        } else if (key in parsed) {
          (merged as any)[key] = parsed[key];
        }
      }
      return merged;
    }
  } catch {
    // Corrupted storage — fall back to defaults
  }
  return JSON.parse(JSON.stringify(DEFAULT_TUNING));
}

/**
 * The live tuning config. Mutated by the TuningPanel.
 * Initialized from localStorage if available, otherwise from defaults.
 */
export const tuning: TuningConfig = loadTuning();

/** Save current tuning values to localStorage */
export function saveTuning(): void {
  try {
    localStorage.setItem(TUNING_STORAGE_KEY, JSON.stringify(tuning));
  } catch {
    // Storage full or unavailable — silently ignore
  }
}

/** Reset all tuning to defaults and clear saved values */
export function resetTuning(): void {
  Object.assign(tuning, JSON.parse(JSON.stringify(DEFAULT_TUNING)));
  try {
    localStorage.removeItem(TUNING_STORAGE_KEY);
  } catch {
    // Silently ignore
  }
}
